import React from 'react';
import { Helmet } from 'react-helmet-async';

const DOMAIN = 'https://aegresearch.in';

const SEO = ({
  title,
  description,
  keywords,
  canonicalPath = '',
  ogType = 'website',
  ogImage = '',
  schemaData = null,
  breadcrumbs = null,
  noindex = false
}) => {
  const siteName = 'ResearchLens | Assurance Education Group';
  const domain = DOMAIN;

  const defaultTitle = 'Best Thesis Writing Services in Bhopal | PhD & MTech Thesis Help';
  const defaultDescription =
    'Assurance Education Group offers the best thesis writing services in Bhopal, MTech & PhD thesis writing, Scientific Research paper writing and publication, IEEE, SCI & Scopus paper writing, plagiarism check, and conference paper publication. Call +91 6268558700.';
  const defaultKeywords = [
    'best thesis writing services in bhopal',
    'Scientific Research',
    'Mtech Thesis Writing',
    'Research paper writing and publication',
    'PhD Thesis writing service and help',
    'Research Paper design and publication',
    'Paper Writing Bhopal',
    'Plagiarism Check Bhopal',
    'Review paper writing bhopal',
    'IEEE paper writing',
    'Scopus paper writing bhopal',
    'SCI paper writing bhopal',
    'Conference paper writing publication',
    'thesis writing Bhopal',
    'dissertation help Bhopal',
    'Assurance Education Group',
    'aegresearch'
  ];

  // Append a short brand suffix, but only when it keeps the title reasonably
  // short and the brand is not already present (Google truncates ~60 chars).
  const brandSuffix = ' | ResearchLens - AEG Group';
  let seoTitle;
  if (!title) {
    seoTitle = defaultTitle;
  } else if (/researchlens|aeg|assurance/i.test(title) || (title.length + brandSuffix.length) > 65) {
    seoTitle = title;
  } else {
    seoTitle = `${title}${brandSuffix}`;
  }
  const seoDescription = description || defaultDescription;

  // Merge caller keywords with defaults, de-duplicated (case-insensitive).
  const callerKeywords = keywords
    ? (Array.isArray(keywords) ? keywords : String(keywords).split(',').map((k) => k.trim()))
    : [];
  const seenKeywords = new Set();
  const seoKeywords = [...callerKeywords, ...defaultKeywords]
    .filter((k) => {
      const key = k.toLowerCase();
      if (!k || seenKeywords.has(key)) return false;
      seenKeywords.add(key);
      return true;
    })
    .join(', ');

  // Normalise the canonical path -> always absolute, no trailing slash except root.
  let currentPath =
    canonicalPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
  if (!currentPath.startsWith('/')) currentPath = `/${currentPath}`;
  currentPath = currentPath.toLowerCase().replace(/\/+$/, '');
  const seoUrl = currentPath === '' ? `${domain}/` : `${domain}${currentPath}`;

  const seoImage = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${domain}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`)
    : `${domain}/ResearchLens.jpeg`;

  const robotsContent = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': ['EducationalOrganization', 'ProfessionalService'],
    '@id': 'https://aegresearch.in/#organization',
    'name': 'Assurance Education Group - ResearchLens',
    'alternateName': ['AEG Group', 'aegresearch', 'AEG Research', 'Assurance Education Group'],
    'url': 'https://aegresearch.in',
    'logo': `${domain}/ResearchLens.jpeg`,
    'image': `${domain}/ResearchLens.jpeg`,
    'telephone': '+916268558700',
    'email': 'aeggrouphelpdesk@gmail.com',
    'priceRange': '$$',
    'description':
      'Best thesis writing services in Bhopal - thesis writing, paper writing, SCI research paper guidance, research paper publication, SCI paper publication and Scopus paper publication.',
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': '329 Kalpana Nagar, BHEL',
      'addressLocality': 'Bhopal',
      'addressRegion': 'Madhya Pradesh',
      'postalCode': '462022',
      'addressCountry': 'IN'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': 23.277311,
      'longitude': 77.418533
    },
    'areaServed': [
      { '@type': 'City', 'name': 'Bhopal' },
      { '@type': 'State', 'name': 'Madhya Pradesh' },
      { '@type': 'Country', 'name': 'India' }
    ],
    'knowsAbout': [
      'Thesis writing',
      'Paper writing',
      'SCI research paper',
      'Research paper publication',
      'SCI paper publication',
      'Scopus paper publication'
    ],
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '5.0',
      'reviewCount': '50',
      'bestRating': '5',
      'worstRating': '1'
    },
    'review': [
      {
        '@type': 'Review',
        'author': {
          '@type': 'Person',
          'name': 'PhD Scholar'
        },
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': '5',
          'bestRating': '5'
        },
        'reviewBody': 'Exceptional thesis writing and SCI paper publication support in Bhopal. Highly professional team and plagiarism-free quality.'
      }
    ],
    'sameAs': [
      'https://www.instagram.com/researchlens',
      'https://www.linkedin.com/company/aeg-consultancy-services-pvt-ltd/',
      'https://www.facebook.com/share/18hJpsqAGz/'
    ]
  };

  const finalSchema = [localBusinessSchema];

  if (breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length) {
    finalSchema.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        'position': index + 1,
        'name': crumb.name,
        'item': crumb.path
          ? `${domain}${crumb.path.startsWith('/') ? '' : '/'}${crumb.path}`
          : undefined
      }))
    });
  }

  if (schemaData) {
    if (Array.isArray(schemaData)) finalSchema.push(...schemaData);
    else finalSchema.push(schemaData);
  }

  return (
    <Helmet>
      <html lang="en-IN" />
      {/* General Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <link rel="canonical" href={seoUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:alt" content="Assurance Education Group - ResearchLens, Bhopal" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      {/* Local SEO Geo Tags */}
      <meta name="geo.region" content="IN-MP" />
      <meta name="geo.placename" content="Bhopal" />
      <meta name="geo.position" content="23.277311;77.418533" />
      <meta name="ICBM" content="23.277311, 77.418533" />

      {/* Structured Schema.org JSON-LD Data */}
      <script type="application/ld+json">{JSON.stringify(finalSchema)}</script>
    </Helmet>
  );
};

export default SEO;
